import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, getDocs,
  writeBatch, serverTimestamp, getDoc
} from 'firebase/firestore';

// ─── Companies ────────────────────────────────────────
export function useCompanies(userId) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setCompanies([]); setLoading(false); return; }
    const q = query(
      collection(db, 'companies'),
      where('userId', '==', userId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      setCompanies(data);
      setLoading(false);
    }, (err) => {
      console.error('Companies listener error:', err);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  const addCompany = useCallback(async (name) => {
    if (!userId) return;
    return addDoc(collection(db, 'companies'), {
      userId,
      name: name.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }, [userId]);

  const updateCompany = useCallback(async (companyId, data) => {
    return updateDoc(doc(db, 'companies', companyId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  }, []);

  const deleteCompany = useCallback(async (companyId) => {
    const rolesSnap = await getDocs(
      query(collection(db, 'roles'), where('companyId', '==', companyId))
    );
    const batch = writeBatch(db);
    for (const roleDoc of rolesSnap.docs) {
      const contactsSnap = await getDocs(
        query(collection(db, 'contacts'), where('roleId', '==', roleDoc.id))
      );
      contactsSnap.docs.forEach(c => batch.delete(c.ref));
      batch.delete(roleDoc.ref);
    }
    batch.delete(doc(db, 'companies', companyId));
    return batch.commit();
  }, []);

  return { companies, loading, addCompany, updateCompany, deleteCompany };
}

// ─── Roles ────────────────────────────────────────────
export function useRoles(userId) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setRoles([]); setLoading(false); return; }
    const q = query(
      collection(db, 'roles'),
      where('userId', '==', userId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      setRoles(data);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  const addRole = useCallback(async (companyId, title) => {
    if (!userId) return;
    return addDoc(collection(db, 'roles'), {
      userId,
      companyId,
      title: title.trim(),
      status: 'Applied',
      notes: '',
      location: '',
      updateMessage: '',
      appliedDate: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }, [userId]);

  const updateRole = useCallback(async (roleId, data) => {
    return updateDoc(doc(db, 'roles', roleId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  }, []);

  const deleteRole = useCallback(async (roleId) => {
    const contactsSnap = await getDocs(
      query(collection(db, 'contacts'), where('roleId', '==', roleId))
    );
    const batch = writeBatch(db);
    contactsSnap.docs.forEach(c => batch.delete(c.ref));
    batch.delete(doc(db, 'roles', roleId));
    return batch.commit();
  }, []);

  return { roles, loading, addRole, updateRole, deleteRole };
}

// ─── Contacts ─────────────────────────────────────────
export function useContacts(userId) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setContacts([]); setLoading(false); return; }
    const q = query(
      collection(db, 'contacts'),
      where('userId', '==', userId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      setContacts(data);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  const addContact = useCallback(async (roleId, companyId, data) => {
    if (!userId) return;
    return addDoc(collection(db, 'contacts'), {
      userId,
      roleId,
      companyId,
      name: data.name.trim(),
      designation: data.designation?.trim() || '',
      linkedinStatus: data.linkedinStatus || 'Pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }, [userId]);

  const updateContact = useCallback(async (contactId, data) => {
    return updateDoc(doc(db, 'contacts', contactId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  }, []);

  const deleteContact = useCallback(async (contactId) => {
    return deleteDoc(doc(db, 'contacts', contactId));
  }, []);

  return { contacts, loading, addContact, updateContact, deleteContact };
}

// ─── Public Profile Fetch ─────────────────────────────
export async function fetchPublicProfile(username) {
  try {
    const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
    if (!usernameDoc.exists()) return null;
    const uid = usernameDoc.data().uid;
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) return null;
    const profile = userDoc.data();

    const companiesSnap = await getDocs(
      query(collection(db, 'companies'), where('userId', '==', uid))
    );
    const companies = companiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const rolesSnap = await getDocs(
      query(collection(db, 'roles'), where('userId', '==', uid))
    );
    const roles = rolesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const contactsSnap = await getDocs(
      query(collection(db, 'contacts'), where('userId', '==', uid))
    );
    const contacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    return { profile, companies, roles, contacts };
  } catch (err) {
    console.error('Error fetching public profile:', err);
    return null;
  }
}

// ─── Stats Calculator ─────────────────────────────────
export function useStats(companies, roles, contacts) {
  const totalCompanies = companies.length;
  const totalRoles = roles.length;
  const totalContacts = contacts.length;
  const connectedContacts = contacts.filter(c => c.linkedinStatus === 'Connected').length;
  const statusCounts = roles.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return { totalCompanies, totalRoles, totalContacts, connectedContacts, statusCounts };
}
